import { isNumber } from 'phoenix2/utils';

export const ALLOW_ANY_SIGN = 'ALLOW_ANY_SIGN';
export const ONLY_NEGATIVE_SIGN = 'ONLY_NEGATIVE_SIGN';
export const ONLY_POSITIVE_SIGN = 'ONLY_POSITIVE_SIGN';

/**
 * NumberEntry - class for creating a validated number that is entered by
 * the user. The number entry will allow characters to be added or removed
 * from the number as long as they obey the number validation rules
 */

export class ValidatedNumberEntry {
    incrementalValidationRules = [
        // It must parse to a valid number, or be empty, or be just a "-"
        // sign with negatives enabled:
        entry => isNumber(entry) || entry === '' || entry === '-',

        // It may not be a decimal if not allowing decimals:
        entry => this.allowDecimal || entry.indexOf('.') === -1,

        // If we allow negative only, entry must be "-", "-0" or < 0
        entry => {
            if (this.signRestriction !== ONLY_NEGATIVE_SIGN) {
                return true;
            }
            return entry.indexOf('-') === 0 && (entry === '-' || +entry <= 0);
        },

        // If we allow positive only, entry must be "", "0" or > 0
        entry => {
            if (this.signRestriction !== ONLY_POSITIVE_SIGN) {
                return true;
            }
            return entry === '' || +entry >= 0;
        },

        // The number of characters must be equal or less than maxCharacters:
        entry => entry.length <= this.maxCharacters,
    ];

    validationRules = [
        // It must parse to a valid number:
        entry => isNumber(entry),

        // The number of digits must be equal or greater than minDigits:
        entry => entry.length >= this.minCharacters,
    ];

    /**
     * Creates a ValidatedNumberEntry from a set of paramters. If no
     * parameters are specified it will allow entry of numbers of up to 12
     * characters, including minus sign and decimal point
     * @param maxCharacters The maximum number of digits (not including minus
     * sign or decimal point, if allowed)
     * @param minCharacters The minimum number of digits (not including minus
     * sign or decimal point, if allowed)
     * @param allowNegative Whether negative numbers should be allowed
     * @param enforceNegative Whether the entry must be <= 0
     * @param allowDecimal Whether decimal numbers should be allowed
     * @param entry The initial entry
     */
    constructor({
        maxCharacters = 12,
        minCharacters = 1,
        allowNegative = true,
        allowDecimal = true,
        enforceNegative = false,
        entry,
    } = {}) {
        this.maxCharacters = maxCharacters;
        this.minCharacters = minCharacters;
        this.allowDecimal = allowDecimal;
        // for enforceNegative, we want to only allow negative values, but we
        // also want to remove the minus key which acts as a toggle for the
        // minus sign on the input value
        this.signRestriction = ALLOW_ANY_SIGN;
        if (enforceNegative) {
            this.signRestriction = ONLY_NEGATIVE_SIGN;
        } else if (!allowNegative) {
            this.signRestriction = ONLY_POSITIVE_SIGN;
        }
        this.entry = entry;

        this.validationRules = this.validationRules.concat(this.incrementalValidationRules);

        if (this._validIncrementalEntry(entry)) {
            this.entry = entry;
        } else {
            this.entry = this.getDefaultEntry();
        }
    }

    getEntry() {
        return this.entry;
    }

    /**
     * Gets the default entry
     */
    getDefaultEntry() {
        return this.signRestriction === ONLY_NEGATIVE_SIGN ? '-' : '';
    }

    /**
     * Whether there any characters in entry which can be deleted. If
     * enforceNegative = true, then we can delete only when the entry is longer
     * than 1 char (i.e. there are chars other than '-'). If
     * enforceNegative = false, then we can delete whenever there are more than
     * 0 chars.
     */
    isBackspaceAllowed() {
        return this.signRestriction === ONLY_NEGATIVE_SIGN
            ? this.entry.length > 1
            : this.entry.length > 0;
    }

    /**
     * Returns true if the current entry is valid according to every
     * validation rule, or false if not
     * @returns {*}
     */
    isValid() {
        return !this.validationRules.some(f => !f(this.entry));
    }

    /**
     * Add a character to the entry. Will only add the character if it would
     * not violate any of the validation rules.
     * @param character A character which is a digit, decimal point, minus
     * sign or backspace
     * @return boolean Returns true if the character was valid and changed
     * the entry, or false if the character had no effect
     */
    applyKeypress(character) {
        let newEntry;
        switch (character) {
            case '0':
            case '1':
            case '2':
            case '3':
            case '4':
            case '5':
            case '6':
            case '7':
            case '8':
            case '9':
                if (this._validIncrementalEntry(this.entry + character)) {
                    this.entry += character;
                    return true;
                }
                break;
            case '.':
                if (this.entry.length === 0 || this.entry === '-') {
                    newEntry = this.entry + '0.';
                } else {
                    newEntry = this.entry + '.';
                }
                if (this._validIncrementalEntry(newEntry)) {
                    this.entry = newEntry;
                    return true;
                }
                break;
            case '-':
                if (this.signRestriction === ALLOW_ANY_SIGN) {
                    // Changes a positive number to negative and vice versa:
                    if (this.entry.length > 0 && this.entry.charAt(0) === '-') {
                        newEntry = this.entry.substr(1);
                    } else {
                        newEntry = '-' + this.entry;
                    }
                    if (this._validIncrementalEntry(newEntry)) {
                        this.entry = newEntry;
                        return true;
                    }
                }
                break;
            case 'Backspace':
                if (this.isBackspaceAllowed()) {
                    newEntry = this.entry.substr(0, this.entry.length - 1);
                    if (this._validIncrementalEntry(newEntry)) {
                        this.entry = newEntry;
                        return true;
                    }
                }
                break;
            default:
                break;
        }
        return false;
    }

    /**
     * Returns true if the given string is a valid beginning part of an
     * entry - i.e. could be a valid entry if zero or more further
     * characters are added
     * @param entry
     * @returns {boolean}
     * @private
     */
    _validIncrementalEntry(entry) {
        return !this.incrementalValidationRules.some(f => !f(entry));
    }
}
